import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  LuPlay as FiPlay, LuUpload as FiUpload, LuSearch as FiSearch,
  LuFolderOpen as FiFolder, LuTag as FiTag, LuShare2 as FiShare2,
  LuUsers as FiUsers, LuFilm as FiFilm, LuDroplets as FiDroplet,
  LuSettings as FiSettings, LuInbox as FiInbox,
  LuCamera as FiCamera, LuImage as FiImage, LuHeart as FiHeart,
  LuLayoutGrid as FiGrid, LuLock as FiLock, LuDownload as FiDownload,
  LuClock as FiClock, LuEye as FiEye, LuCircleCheck as FiCheckCircle,
  LuStar as FiStar, LuSlidersHorizontal as FiSliders,
  LuSmartphone as FiSmartphone, LuShieldCheck as FiShield,
  LuKeyRound as FiKey, LuZap as FiZap, LuMessageSquare as FiMessageSquare,
  LuUser as FiUser, LuMoon as FiMoon, LuSun as FiSun,
  LuCreditCard as FiCreditCard, LuHardDrive as FiHardDrive,
  LuChevronRight as FiChevronRight, LuMenu as FiMenu, LuX as FiX,
  LuInfo as FiInfo
} from 'react-icons/lu';
import PublicLayout from './PublicLayout';
import { HEIF_AND_PRO_STILL_EXTENSIONS, RAW_EXTENSIONS, STANDARD_EXTENSIONS } from '../utils/supportedExtensions';

const TOC = [
  { id: 'getting-started', label: 'Getting Started', icon: FiPlay },
  { id: 'uploading', label: 'Uploading Photos', icon: FiUpload },
  { id: 'browsing', label: 'Browsing & Searching', icon: FiSearch },
  { id: 'albums', label: 'Albums & Organization', icon: FiFolder },
  { id: 'tags', label: 'Tags', icon: FiTag },
  { id: 'sharing', label: 'Sharing Photos', icon: FiShare2 },
  { id: 'clients', label: 'Client Galleries & Proofing', icon: FiInbox },
  { id: 'strip', label: 'The Strip', icon: FiFilm },
  { id: 'watermarks', label: 'Watermarks', icon: FiDroplet },
  { id: 'team', label: 'Team & Collaboration', icon: FiUsers },
  { id: 'settings', label: 'Account & Settings', icon: FiSettings },
];

function GuideSection({ id, icon: Icon, title, intro, tier, children }) {
  return (
    <section className="guide-section" id={id}>
      <div className="guide-section-header">
        <div className="guide-section-icon"><Icon size={22} /></div>
        <div>
          <h2>{title}{tier && <span className="guide-tier-badge">{tier}</span>}</h2>
          {intro && <p className="guide-section-intro">{intro}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

function Step({ number, title, children }) {
  return (
    <div className="guide-step">
      <span className="guide-step-num">{number}</span>
      <div>
        <strong>{title}</strong>
        <div className="guide-step-body">{children}</div>
      </div>
    </div>
  );
}

function Tip({ children }) {
  return (
    <div className="guide-tip">
      <FiInfo size={16} />
      <div>{children}</div>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, children }) {
  return (
    <div className="guide-card">
      {Icon && <div className="guide-card-icon"><Icon size={18} /></div>}
      <h4>{title}</h4>
      <div>{children}</div>
    </div>
  );
}

export default function Guide() {
  const [activeSection, setActiveSection] = useState('getting-started');
  const [tocOpen, setTocOpen] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        }
      },
      { rootMargin: '-80px 0px -70% 0px' }
    );
    for (const item of TOC) {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setTocOpen(false);
  }, [activeSection]);

  useEffect(() => {
    document.title = 'Guide | Photo Harbor';
  }, []);

  return (
    <PublicLayout>
    <div className="guide-page guide-page--lp">
      <section className="lp-hero" style={{ paddingBottom: '2rem' }}>
        <div className="lp-hero-inner">
          <h1 className="lp-h1">How to Use Photo Harbor</h1>
          <p className="lp-hero-sub">A complete guide to storing, organizing, sharing, and delivering your photography.</p>
        </div>
      </section>

      <div className="guide-layout">
        {/* Mobile ToC toggle */}
        <button
          className="guide-toc-toggle"
          onClick={() => setTocOpen(!tocOpen)}
          aria-label="Toggle table of contents"
        >
          {tocOpen ? <FiX size={18} /> : <FiMenu size={18} />}
          <span>Jump to section</span>
        </button>

        <nav className={`guide-toc${tocOpen ? ' guide-toc--open' : ''}`}>
          <div className="guide-toc-title">Contents</div>
          {TOC.map(({ id, label, icon: Icon }) => (
            <a
              key={id}
              href={`#${id}`}
              className={`guide-toc-link${activeSection === id ? ' active' : ''}`}
              onClick={() => setTocOpen(false)}
            >
              <Icon size={14} />
              <span>{label}</span>
            </a>
          ))}
        </nav>

        <div className="guide-content">

          {/* ============ GETTING STARTED ============ */}
          <GuideSection
            id="getting-started"
            icon={FiPlay}
            title="Getting Started"
            intro="Create your account and get oriented with the Photo Harbor interface."
          >
            <div className="guide-steps">
              <Step number="1" title="Create your account">
                <p>Visit <a href="https://photos.dailey.cloud">photos.dailey.cloud</a> and sign up with your email address. Every plan starts with a 14-day free trial. Your account is secured by Dailey Core, our zero-trust authentication system. You can also register a passkey (fingerprint or hardware key) for faster, passwordless login.</p>
              </Step>
              <Step number="2" title="Navigate the interface">
                <p>On desktop, use the sidebar (app rail) on the left to switch between sections. On mobile, use the bottom tab bar for quick access to Photos, Albums, Shares, and a Menu button that opens all navigation options.</p>
              </Step>
              <Step number="3" title="Know the main areas">
                <p><strong>Photos</strong> is your full library. <strong>Albums</strong> lets you organize into collections. <strong>Shares</strong> manages your share links. <strong>Favorites</strong> collects everything you've hearted. <strong>Settings</strong> handles your profile, billing, and preferences.</p>
              </Step>
            </div>

            <div className="guide-cards">
              <FeatureCard icon={FiShield} title="Secured by Dailey Core">
                <p>Your login is handled by our centralized auth system with encrypted sessions, passkey support, and zero-trust token validation. One secure account across all Dailey apps.</p>
              </FeatureCard>
              <FeatureCard icon={FiSmartphone} title="Works everywhere">
                <p>Photo Harbor is fully responsive. The same features work on desktop, tablet, and phone with layouts optimized for each screen size.</p>
              </FeatureCard>
            </div>
          </GuideSection>

          {/* ============ UPLOADING ============ */}
          <GuideSection
            id="uploading"
            icon={FiUpload}
            title="Uploading Photos"
            intro="Get your photos into Photo Harbor with batch uploads, RAW support, and background processing."
          >
            <div className="guide-steps">
              <Step number="1" title="Start an upload">
                <p>Click the <strong>Upload</strong> button in the top bar, or drag and drop files directly onto the photo grid. You can select multiple files at once from your file browser.</p>
              </Step>
              <Step number="2" title="Choose your upload speed">
                <p>Pick the mode that matches your connection:</p>
                <ul>
                  <li><strong>Fast</strong>: 15 concurrent uploads. Best on strong broadband or fiber.</li>
                  <li><strong>Balanced</strong>: 4 concurrent uploads. Good default for most connections.</li>
                  <li><strong>Stable</strong>: 1 at a time. Best for slower or unreliable networks.</li>
                </ul>
                <p>Your speed preference is saved for next time.</p>
              </Step>
              <Step number="3" title="Monitor progress">
                <p>Each file shows its own progress bar. If a file fails, you'll see the error reason and can retry it. You can also minimize the upload panel and keep browsing your library while uploads continue in the background.</p>
              </Step>
            </div>

            <h3>Supported Formats</h3>
            <div className="guide-format-group">
              <div className="guide-format-category">
                <strong>Standard</strong>
                <div className="guide-format-tags">
                  {[...STANDARD_EXTENSIONS, ...HEIF_AND_PRO_STILL_EXTENSIONS].map((ext) => (
                    <span key={ext} className="guide-fmt">{ext.replace(/^\./, '').toUpperCase()}</span>
                  ))}
                </div>
              </div>
              <div className="guide-format-category">
                <strong>RAW</strong>
                <div className="guide-format-tags">
                  {RAW_EXTENSIONS.map((ext) => (
                    <span key={ext} className="guide-fmt guide-fmt--raw">{ext.replace(/^\./, '').toUpperCase()}</span>
                  ))}
                </div>
              </div>
            </div>

            <Tip>
              <strong>RAW processing:</strong> When you upload a RAW file, Dailey Burst automatically generates a high-quality JPEG preview in the background. Your original RAW is preserved exactly as uploaded. The preview appears once processing completes, no action needed on your part.
            </Tip>

            <Tip>
              <strong>File size limit:</strong> Individual files can be up to 250MB each. There's no limit on how many files you upload in a batch.
            </Tip>
          </GuideSection>

          {/* ============ BROWSING & SEARCHING ============ */}
          <GuideSection
            id="browsing"
            icon={FiSearch}
            title="Browsing & Searching"
            intro="Find any photo in your library with search, filters, and metadata browsing."
          >
            <div className="guide-cards">
              <FeatureCard icon={FiGrid} title="Photo Grid">
                <p>Browse your library in a responsive grid. Use the tile size slider to zoom in (larger thumbnails) or out (more photos visible). Switch between 50 and 100 photos per page.</p>
              </FeatureCard>
              <FeatureCard icon={FiSearch} title="Search">
                <p>Type in the search bar to find photos by filename or keyword. Results update as you type with a brief debounce for performance.</p>
              </FeatureCard>
              <FeatureCard icon={FiSliders} title="Library Filters">
                <p>Filter your library by file type, camera model, lens, focal length range, ISO range, and date range. Active filters show as removable chips. Combine multiple filters to narrow down exactly what you're looking for.</p>
              </FeatureCard>
              <FeatureCard icon={FiHeart} title="Favorites">
                <p>Click the heart icon on any photo to mark it as a favorite. Toggle the Favorites filter to see only your hearted photos. Favorites also appear as a special collection in your Albums view.</p>
              </FeatureCard>
            </div>

            <h3>EXIF Metadata</h3>
            <p>Every photo displays its embedded camera metadata. Click a photo to see details including:</p>
            <ul className="guide-detail-list">
              <li><FiCamera size={14} /> <strong>Camera</strong>: Make and model (e.g., Canon EOS R5)</li>
              <li><FiImage size={14} /> <strong>Lens</strong>: Lens model and focal length</li>
              <li><FiZap size={14} /> <strong>Exposure</strong>: ISO, aperture (f-number), and shutter speed</li>
              <li><FiClock size={14} /> <strong>Date</strong>: Original capture date and time from the file</li>
            </ul>
          </GuideSection>

          {/* ============ ALBUMS ============ */}
          <GuideSection
            id="albums"
            icon={FiFolder}
            title="Albums & Organization"
            intro="Group your photos into collections for projects, events, or any way you like."
          >
            <div className="guide-steps">
              <Step number="1" title="Create an album">
                <p>Click <strong>New Album</strong> on the Albums page. Give it a title and optional description. The first photo you add becomes the cover image automatically.</p>
              </Step>
              <Step number="2" title="Add photos">
                <p>From your photo library, select photos and use the "Add to Album" action. You can also upload directly into an album.</p>
              </Step>
              <Step number="3" title="Organize within an album">
                <p>Inside an album, drag photos to reorder them. The order you set is preserved and used when sharing. Rename the album or update the description anytime.</p>
              </Step>
            </div>

            <div className="guide-cards">
              <FeatureCard icon={FiImage} title="Album Covers">
                <p>The first image in your album serves as the cover. Reorder photos to change which one appears as the cover in your album list.</p>
              </FeatureCard>
              <FeatureCard icon={FiGrid} title="Sorting & Previews">
                <p>Sort your album list by <strong>Recent</strong> (last updated) or <strong>A-Z</strong> (alphabetical). Hover over any album card to see a quick preview of up to 8 photos inside.</p>
              </FeatureCard>
            </div>

            <Tip>
              <strong>Album limits:</strong> Personal tier allows up to 25 albums. Pro tier and above have unlimited albums.
            </Tip>
          </GuideSection>

          {/* ============ TAGS ============ */}
          <GuideSection
            id="tags"
            icon={FiTag}
            title="Tags"
            intro="Label and categorize photos across albums with color-coded tags."
          >
            <div className="guide-steps">
              <Step number="1" title="Create a tag">
                <p>Go to the <strong>Tags</strong> page and click <strong>Create Tag</strong>. Give it a name (e.g., "Portraits", "Edited", "Favorites 2024") and pick a color from 18 presets.</p>
              </Step>
              <Step number="2" title="Apply tags to photos">
                <p>Select one or more photos in your library, then use the tag selector to apply tags. You can apply multiple tags to a single photo.</p>
              </Step>
              <Step number="3" title="Browse by tag">
                <p>Switch to <strong>Browse</strong> mode on the Tags page to see visual previews of each tag. Click a tag to view all photos with that label. Use the lightbox viewer with arrow key navigation to flip through them.</p>
              </Step>
            </div>

            <Tip>Tags work across albums: a photo can belong to an album and have multiple tags simultaneously. Tags are a great way to mark editing status, photo type, or any cross-cutting category.</Tip>
          </GuideSection>

          {/* ============ SHARING ============ */}
          <GuideSection
            id="sharing"
            icon={FiShare2}
            title="Sharing Photos"
            intro="Create secure share links with granular controls over who can see and download your work."
          >
            <div className="guide-steps">
              <Step number="1" title="Create a share link">
                <p>Open an album and click <strong>Share</strong>. This generates a unique link that anyone with the URL can use to view the album. No account required for viewers.</p>
              </Step>
              <Step number="2" title="Set controls">
                <p>Configure your share with these options:</p>
                <ul>
                  <li><strong>Expiration date</strong>: Link automatically stops working after this date</li>
                  <li><strong>Download permission</strong>: Allow or prevent viewers from downloading the album as a ZIP</li>
                  <li><strong>Watermark</strong>: Apply your watermark to all photos in the shared view</li>
                </ul>
              </Step>
              <Step number="3" title="Share and track">
                <p>Copy the link and send it however you like: email, text, social media. Track how many people have viewed the share and when from your <strong>Shares</strong> dashboard.</p>
              </Step>
            </div>

            <div className="guide-cards">
              <FeatureCard icon={FiEye} title="Share Analytics">
                <p>See view counts, last viewed timestamps, and download activity for each share link. Know exactly when and how often your shares are being accessed.</p>
              </FeatureCard>
              <FeatureCard icon={FiLock} title="Revoke Access">
                <p>Revoke a share link at any time to immediately disable access. You can also reinstate a revoked share, which generates a new token (the old URL will no longer work).</p>
              </FeatureCard>
            </div>
          </GuideSection>

          {/* ============ CLIENT GALLERIES ============ */}
          <GuideSection
            id="clients"
            icon={FiInbox}
            title="Client Galleries & Proofing"
            tier="Studio+"
            intro="Deliver proofs to clients, collect selections, and manage the review process from start to finish."
          >
            <div className="guide-steps">
              <Step number="1" title="Create a client gallery">
                <p>From an album, create a share with <strong>Client Mode</strong> enabled. Set a password that your client will use to access the gallery. Add optional instructions (e.g., "Please select your favorite 20 images").</p>
              </Step>
              <Step number="2" title="Client views and selects">
                <p>Your client opens the link, enters the password, and sees the gallery. They can browse all photos, and if you've set up a selection request, they can pick their favorites with a visual counter showing their progress against the selection limits you set.</p>
              </Step>
              <Step number="3" title="Review submissions">
                <p>When the client submits their selections, you'll see them in the <strong>Submissions</strong> section. Review their picks, update the status (reviewed, approved, rejected), and download the selected photos as a ZIP.</p>
              </Step>
            </div>

            <div className="guide-cards">
              <FeatureCard icon={FiCheckCircle} title="Selection Limits">
                <p>Set minimum and maximum selection counts to guide your client. They'll see a real-time counter and numbered badges on selected photos showing their pick order.</p>
              </FeatureCard>
              <FeatureCard icon={FiMessageSquare} title="Client Comments">
                <p>Clients can leave comments on individual photos. You'll see all comments in your album detail view for review. Comments go through a status workflow, pending, approved, rejected, or flagged.</p>
              </FeatureCard>
              <FeatureCard icon={FiDownload} title="Download Selections">
                <p>After a client submits, download all their selected photos as a single ZIP file directly from the submission review page.</p>
              </FeatureCard>
            </div>

            <h3>Submission Workflow</h3>
            <div className="guide-workflow">
              <div className="guide-workflow-step">
                <span className="guide-workflow-badge">1</span>
                <span>Client views gallery</span>
              </div>
              <FiChevronRight size={16} className="guide-workflow-arrow" />
              <div className="guide-workflow-step">
                <span className="guide-workflow-badge">2</span>
                <span>Client selects photos</span>
              </div>
              <FiChevronRight size={16} className="guide-workflow-arrow" />
              <div className="guide-workflow-step">
                <span className="guide-workflow-badge">3</span>
                <span>Submitted</span>
              </div>
              <FiChevronRight size={16} className="guide-workflow-arrow" />
              <div className="guide-workflow-step">
                <span className="guide-workflow-badge">4</span>
                <span>Reviewed</span>
              </div>
              <FiChevronRight size={16} className="guide-workflow-arrow" />
              <div className="guide-workflow-step">
                <span className="guide-workflow-badge">5</span>
                <span>Approved</span>
              </div>
            </div>
          </GuideSection>

          {/* ============ THE STRIP ============ */}
          <GuideSection
            id="strip"
            icon={FiFilm}
            title="The Strip"
            tier="Business+"
            intro="A private social feed for sharing photos with friends. No algorithms, no ads, no nonsense."
          >
            <div className="guide-steps">
              <Step number="1" title="Post to The Strip">
                <p>Share a single photo or an entire album to The Strip. Add a caption to give context. Your post is visible only to your connected friends.</p>
              </Step>
              <Step number="2" title="Interact with posts">
                <p>Like posts with the heart button and leave comments. See who liked your photos and how many total likes you've received across all posts.</p>
              </Step>
              <Step number="3" title="Build your network">
                <p>Connect with other photographers via the <strong>Friends</strong> feature. Search by username or email, send a friend request, and once connected you'll see each other's Strip posts.</p>
              </Step>
            </div>

            <Tip>The Strip is designed to be the opposite of algorithmic social media. There's no feed manipulation, no promotion, no advertising. Just photos from people you actually know.</Tip>
          </GuideSection>

          {/* ============ WATERMARKS ============ */}
          <GuideSection
            id="watermarks"
            icon={FiDroplet}
            title="Watermarks"
            tier="Pro+"
            intro="Protect your work with custom watermarks that can be applied automatically to shared photos."
          >
            <div className="guide-steps">
              <Step number="1" title="Upload your watermark">
                <p>Go to the <strong>Watermark</strong> page and upload a PNG image (transparency recommended for best results). This is typically your logo or brand mark.</p>
              </Step>
              <Step number="2" title="Position for each orientation">
                <p>Set separate watermark positions for portrait and landscape photos. Choose from 9 preset positions (corners, edges, center) or drag to place it exactly where you want. Adjust the size as a percentage of image width (5-50%).</p>
              </Step>
              <Step number="3" title="Configure style">
                <p>Set the opacity (0-100%) and choose a style effect:</p>
                <ul>
                  <li><strong>None</strong>: Flat watermark, no effects</li>
                  <li><strong>Outline</strong>: White outline with shadow for contrast on any background</li>
                  <li><strong>Shadow</strong>: Drop shadow for subtle depth</li>
                </ul>
              </Step>
              <Step number="4" title="Test and save">
                <p>Upload a test image to preview exactly how your watermark will look on real photos. Once satisfied, save your configuration. Toggle the watermark on when creating share links.</p>
              </Step>
            </div>
          </GuideSection>

          {/* ============ TEAM ============ */}
          <GuideSection
            id="team"
            icon={FiUsers}
            title="Team & Collaboration"
            tier="Pro+"
            intro="Invite team members, manage permissions, and collaborate on projects together."
          >
            <div className="guide-steps">
              <Step number="1" title="Invite a team member">
                <p>Go to <strong>Settings &rarr; Team</strong> and invite people by email or generate an invitation link. They'll receive an invite to join your workspace.</p>
              </Step>
              <Step number="2" title="Set album permissions">
                <p>Control what each team member can do on a per-album basis:</p>
                <ul>
                  <li><strong>View</strong>: Can see the album and its photos</li>
                  <li><strong>Edit</strong>: Can add, remove, or reorder photos</li>
                  <li><strong>Share</strong>: Can create and manage share links for the album</li>
                </ul>
              </Step>
              <Step number="3" title="Share albums with your team">
                <p>Mark albums as <strong>Team Visible</strong> to make them available to all team members in your workspace.</p>
              </Step>
            </div>

            <h3>Included Seats by Tier</h3>
            <table className="guide-table">
              <thead>
                <tr><th>Tier</th><th>Included Seats</th><th>Extra Seats</th></tr>
              </thead>
              <tbody>
                <tr><td>Pro</td><td>1</td><td>$8/seat/month</td></tr>
                <tr><td>Studio</td><td>1</td><td>$8/seat/month</td></tr>
                <tr><td>Business</td><td>5</td><td>$8/seat/month</td></tr>
                <tr><td>Agency</td><td>20</td><td>$8/seat/month</td></tr>
              </tbody>
            </table>
          </GuideSection>

          {/* ============ ACCOUNT & SETTINGS ============ */}
          <GuideSection
            id="settings"
            icon={FiSettings}
            title="Account & Settings"
            intro="Manage your profile, security, appearance, and subscription."
          >
            <div className="guide-cards">
              <FeatureCard icon={FiUser} title="Profile">
                <p>Update your display name, username (checked for availability), email, and profile photo. Your username is how other users find you on The Strip and Friends.</p>
              </FeatureCard>
              <FeatureCard icon={FiKey} title="Security">
                <p>Change your password or register a <strong>passkey</strong> (fingerprint, Face ID, or hardware security key) for faster, more secure login. Passkeys use the WebAuthn standard, no passwords to remember.</p>
              </FeatureCard>
              <FeatureCard icon={FiMoon} title="Appearance">
                <p>Choose between <strong>Light</strong>, <strong>Dark</strong>, or <strong>Auto</strong> (follows your system preference) themes. Your choice is saved and applied instantly.</p>
              </FeatureCard>
              <FeatureCard icon={FiSliders} title="Feature Toggles">
                <p>Enable or disable major feature areas to simplify your interface: <strong>Clients</strong>, <strong>Shares</strong>, <strong>Friends</strong>, and <strong>The Strip</strong>. Disabled features are hidden from your navigation.</p>
              </FeatureCard>
            </div>

            <h3>Billing & Storage</h3>
            <p>View your current subscription tier, storage usage, and manage upgrades from the Billing section in Settings.</p>

            <table className="guide-table">
              <thead>
                <tr><th>Tier</th><th>Price</th><th>Storage</th><th>Key Features</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td>Personal</td><td>$7/mo</td><td>100 GB</td>
                  <td>Private storage, sharing, RAW support</td>
                </tr>
                <tr>
                  <td>Pro</td><td>$39/mo</td><td>500 GB</td>
                  <td>+ Watermarks, batch rename, analytics, team seats</td>
                </tr>
                <tr>
                  <td>Studio</td><td>$99/mo</td><td>2 TB</td>
                  <td>+ Client galleries, proofing, priority previews</td>
                </tr>
                <tr>
                  <td>Business</td><td>$179/mo</td><td>5 TB</td>
                  <td>+ Client uploads, jobs, priority compute, 5 seats</td>
                </tr>
                <tr>
                  <td>Agency</td><td>$399/mo</td><td>12 TB</td>
                  <td>+ White label, API, SLA, 20 seats, priority support</td>
                </tr>
              </tbody>
            </table>

            <div className="guide-cards" style={{ marginTop: '24px' }}>
              <FeatureCard icon={FiHardDrive} title="Storage Add-ons">
                <p>Need more space? Add storage at <strong>$29/TB/month</strong>. Personal tier supports up to 2TB of add-ons. Pro and above support unlimited add-on storage.</p>
              </FeatureCard>
              <FeatureCard icon={FiCreditCard} title="Cold Archive">
                <p>Move rarely-accessed photos to cold archive storage at <strong>$19/TB/month</strong>. Retrieval takes 12-48 hours. Requires an active subscription.</p>
              </FeatureCard>
            </div>
          </GuideSection>

          {/* CTA */}
          <div className="guide-cta">
            <h2>Ready to get started?</h2>
            <p>Start your 14-day free trial and begin uploading.</p>
            <div className="guide-cta-actions">
              <Link to="/login" className="guide-cta-btn primary">Sign Up</Link>
              <Link to="/features" className="guide-cta-btn secondary">Explore Features</Link>
            </div>
          </div>

        </div>
      </div>
    </div>
    </PublicLayout>
  );
}
